<template>
  <component :is="embedded ? 'div' : 'v-app'" class="whatsapp-root" :class="{ 'whatsapp-embedded': embedded }">
    <div class="whatsapp-container">
      <div class="whatsapp-main">
        <!-- Left Panel (Conversas) -->
        <div class="left-panel">
          <!-- Profile Header -->
          <div class="profile-header">
            <p style="font-size: 23px; font-weight: 600; margin-top: 10px; color: #1DAA61;">Sicon Chat</p>
            <v-avatar size="30" class="profile-avatar">
              <v-img src="https://via.placeholder.com/40" alt="Profile" />
            </v-avatar>
          </div>

          <!-- Search Bar -->
          <div class="search-container">
            <div class="search-box">
              <v-icon size="13" color="#54656f" class="search-icon">fas fa-search</v-icon>
              <input v-model="searchQuery" type="text" placeholder="Pesquisar ou começar uma nova conversa"
                     class="search-input" />
            </div>
          </div>

          <!-- Filter Tabs -->
          <div class="filter-tabs">
            <div v-for="filter in filters" :key="filter.value"
                 :class="['filter-tab', { 'active': selectedFilter === filter.value }]"
                 @click="selectedFilter = filter.value">
              {{ filter.label }}
              <v-badge style="position: absolute; transform: translate(35px, -15px);" 
                       v-if="filter.count > 0"
                       :content="filter.count" color="#25d366" inline text-color="white" />
            </div>
          </div>

          <!-- Loading / Error / List -->
          <div v-if="loading" class="loading-container">
            <v-progress-circular indeterminate color="#25d366" size="40" />
            <p class="loading-text">Carregando conversas...</p>
          </div>

          <div v-else-if="error" class="error-container">
            <v-icon size="48" color="#f44336">fas fa-exclamation-triangle</v-icon>
            <p class="error-text">{{ error }}</p>
            <v-btn @click="fetchChats" color="#25d366" variant="outlined" size="small">
              Tentar novamente
            </v-btn>
          </div>

          <div v-else class="conversations-container">
            <div v-for="conv in filteredConversations" :key="conv.id"
                 :class="['conversation-item', { 'selected': selectedConversation?.id === conv.id }]"
                 @click="selectConversation(conv)">
              <v-avatar size="50" class="conversation-avatar">
                <v-img :src="generateAvatar(conv.name, conv.avatar)" />
              </v-avatar>
              <div class="conversation-content">
                <div class="conversation-header">
                  <div class="conversation-name">
                    {{ conv.title || conv.name || cleanContactName(conv.id) }}
                  <v-chip v-if="conv.isTransferred && conv.transferredFrom" 
                          size="x-small" 
                          color="orange" 
                          variant="outlined" 
                          class="ml-2">
                    <v-icon size="10" class="mr-1">fas fa-exchange-alt</v-icon>
                    de {{ conv.transferredFrom }}
                  </v-chip>
                  </div>
                </div>
                <div class="conversation-footer">
                  <div class="last-message">
                    <v-icon v-if="conv.fromMe && conv.lastMessageStatus" 
                            :color="getStatusColor(conv.lastMessageStatus)"
                            size="16" class="status-icon">
                      {{ getStatusIcon(conv.lastMessageStatus) }}
                    </v-icon>
                    <span v-if="conv.fromMe" class="you-indicator">
                      Você: 
                    </span>
                    <span class="message-text">
                    {{ getLastMessagePreview(conv) }}
                  </span>
                  </div>
                  <div class="conversation-badges">
                    <v-badge v-if="conv.unreadCount > 0" 
                             :content="conv.unreadCount > 99 ? '99+' : conv.unreadCount.toString()"
                             color="#25d366"
                             class="unread-badge"
                             inline />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Panel (Chat) -->
        <div class="right-panel">
          <div v-if="selectedConversation" class="chat-container">
            <!-- Header -->
            <div class="chat-header">
              <div class="chat-contact-info">
                <v-avatar size="50" class="mr-3">
                  <v-img :src="normalizeAvatarUrl(selectedConversation.avatar, selectedConversation.name || selectedConversation.title || cleanContactName(selectedConversation.id))" />
                </v-avatar>
                <div class="contact-details">
                  <div class="contact-name">
                    {{ selectedConversation.title || selectedConversation.name || cleanContactName(selectedConversation.id) }}
                    <v-chip v-if="selectedConversation.department" 
                            size="x-small" color="primary" variant="outlined" class="ml-2">
                      {{ selectedConversation.department.name }}
                    </v-chip>
                  </div>
                  <div class="contact-status">
                    {{ selectedConversation.isOnline ? 'online' : getLastSeen() }}
                  </div>
                </div>
              </div>
              <div class="chat-actions">
                <v-btn icon variant="text" size="small"><v-icon size="20">fas fa-search</v-icon></v-btn>
                <v-menu>
                  <template v-slot:activator="{ props }">
                    <v-btn icon variant="text" size="small" v-bind="props">
                      <v-icon size="20">fas fa-ellipsis-v</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="openTransferDialog">
                      <v-list-item-title>
                        <v-icon size="16" class="mr-2">fas fa-exchange-alt</v-icon>
                        Transferir conversa
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="showTransferHistory = true">
                      <v-list-item-title>
                        <v-icon size="16" class="mr-2">fas fa-history</v-icon>
                        Histórico de transferências
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="selectedConversation?.isTransferred" @click="removeTransferMark">
                      <v-list-item-title>
                        <v-icon size="16" class="mr-2">fas fa-undo-alt</v-icon>
                        Remover marca de transferência
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>

            <!-- Transfer Alert -->
            <v-alert v-if="selectedConversation.isTransferred" 
                     type="info" 
                     variant="outlined" 
                     class="mx-4 mt-2 mb-0">
              <template #prepend>
                <v-icon>fas fa-exchange-alt</v-icon>
              </template>
              <div class="text-body-2">
                Esta conversa foi transferida para {{ getConversationTransferredTo(selectedConversation) }}
              </div>
              <div v-if="selectedConversation.transferMessage" class="text-caption mt-1">
                {{ selectedConversation.transferMessage }}
              </div>
            </v-alert>

            <!-- Mensagens -->
            <div class="chat-messages">
              <div v-if="messagesLoading" class="messages-loading">
                <v-progress-circular indeterminate color="#25d366" size="34" />
                <span>Carregando histórico...</span>
              </div>

              <div v-else-if="messagesError" class="messages-error">
                <v-icon size="28" color="#f44336">fas fa-exclamation-triangle</v-icon>
                <span>{{ messagesError }}</span>
                <v-btn size="small" variant="outlined" color="#25d366" @click="fetchChatHistory(selectedConversation?.id, true)">
                  Tentar novamente
                </v-btn>
              </div>

              <div v-else class="messages-container" ref="messagesContainer" @scroll.passive="handleScroll">
                <div v-for="msg in messages" :key="msg.id" :class="['message', msg.fromMe ? 'sent' : 'received']">
                  <div class="message-content">
                    <!-- Renderização baseada no tipo da mensagem -->
                    <template v-if="msg.hasMedia">
                      <!-- IMAGENS -->
                      <template v-if="msg.type === 'image' || msg.type === 'imageMessage'">
                        <div class="message-image">
                          <img 
                            :src="getMediaUrl(msg)" 
                            :alt="msg.media?.filename || 'Imagem'"
                            style="max-width: 250px; max-height: 300px; border-radius: 8px; cursor: pointer;"
                            @click="openImageViewer(msg)"
                            @error="handleImageError"
                          />
                          <div v-if="msg.content && msg.content.trim()" class="image-caption">
                            {{ msg.content }}
                          </div>
                        </div>
                      </template>

                      <!-- ÁUDIOS -->
                      <template v-else-if="msg.type === 'audio' || msg.type === 'ptt'">
                        <div class="message-audio">
                          <div class="audio-player">
                            <v-btn 
                              icon 
                              size="small" 
                              @click="toggleAudio(msg.id)"
                              :color="isPlaying(msg.id) ? '#25d366' : '#54656f'"
                            >
                              <v-icon size="16">
                                {{ isPlaying(msg.id) ? 'fas fa-pause' : 'fas fa-play' }}
                              </v-icon>
                            </v-btn>
                            
                            <audio 
                              :ref="`audio-${msg.id}`"
                              :src="getMediaUrl(msg)"
                              @ended="audioEnded(msg.id)"
                              @loadedmetadata="audioLoaded(msg.id, $event)"
                              style="display: none;"
                            ></audio>
                            
                            <div class="audio-waveform">
                              <div class="audio-progress" :style="{ width: getAudioProgress(msg.id) + '%' }"></div>
                            </div>
                            
                            <span class="audio-duration">
                              {{ getAudioDuration(msg) }}
                            </span>
                            
                            <v-icon v-if="msg.type === 'ptt'" size="16" color="#25d366" class="ml-2">
                              fas fa-microphone
                            </v-icon>
                          </div>
                        </div>
                      </template>

                      <!-- VÍDEOS -->
                      <template v-else-if="msg.type === 'video'">
                        <div class="message-video">
                          <video 
                            :src="getMediaUrl(msg)"
                            controls
                            style="max-width: 250px; max-height: 300px; border-radius: 8px;"
                            preload="metadata"
                          >
                            Seu navegador não suporta vídeo.
                          </video>
                          <div v-if="msg.content && msg.content.trim()" class="video-caption">
                            {{ msg.content }}
                          </div>
                        </div>
                      </template>

                      <!-- DOCUMENTOS -->
                      <template v-else-if="msg.type === 'document'">
                        <div class="message-document">
                          <v-card variant="outlined" class="document-card">
                            <v-card-text class="pa-3">
                              <div class="d-flex align-center">
                                <v-icon size="24" color="#54656f" class="mr-3">fas fa-file</v-icon>
                                <div class="flex-grow-1">
                                  <div class="text-body-2 font-weight-medium">
                                    {{ msg.media?.filename || 'Documento' }}
                                  </div>
                                  <div class="text-caption text-medium-emphasis">
                                    {{ getFileSize(msg.media?.data) }}
                                  </div>
                                </div>
                                <v-btn 
                                  icon 
                                  size="small" 
                                  :href="getMediaUrl(msg)"
                                  :download="msg.media?.filename"
                                  target="_blank"
                                >
                                  <v-icon size="16">fas fa-download</v-icon>
                                </v-btn>
                              </div>
                            </v-card-text>
                          </v-card>
                          <div v-if="msg.content && msg.content.trim()" class="document-caption">
                            {{ msg.content }}
                          </div>
                        </div>
                      </template>

                      <!-- STICKERS -->
                      <template v-else-if="msg.type === 'sticker'">
                        <div class="message-sticker">
                          <img 
                            :src="getMediaUrl(msg)"
                            alt="Sticker"
                            style="max-width: 150px; border-radius: 0;"
                            @error="handleImageError"
                          />
                        </div>
                      </template>

                      <!-- TIPO DE MÍDIA NÃO SUPORTADO -->
                      <template v-else>
                        <div class="message-unsupported">
                          <v-icon size="20" class="mr-2">fas fa-question-circle</v-icon>
                          Tipo de mídia não suportado: {{ msg.type }}
                        </div>
                      </template>
                    </template>

                    <!-- MENSAGEM DE TEXTO -->
                    <template v-else>
                      <div class="message-text">{{ msg.content }}</div>
                    </template>

                    <div class="message-time">
                      {{ formatClock(msg.timestamp) }}
                      <v-icon v-if="msg.fromMe" size="14" color="#53bdeb" class="ml-1">
                        fas fa-check-double
                      </v-icon>
                    </div>
                  </div>
                </div>
              </div>

              <button
                v-if="showScrollButton && hasNewMessages"
                class="new-message-box"
                @click="scrollToBottom"
                type="button"
              >
                <v-icon size="14" class="mr-1">fas fa-arrow-down</v-icon>
                Nova mensagem
              </button>
            </div>

            <!-- Input MELHORADO -->
            <div :class="['chat-input', 'enhanced-input', { 
              'input-focused': inputFocused, 
              'has-text': messageText.trim(),
              'is-typing': isTyping 
            }]">

            <input 
              ref="fileInput"
              type="file" 
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
              @change="handleFileSelect"
              style="display: none"
              multiple
            />
              
              <!-- Indicador de digitação -->
              <div class="typing-indicator" :class="{ 'active': isTyping }">
                <div class="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                Digitando...
              </div>

              <!-- Botão emoji melhorado -->
              <v-btn 
                icon 
                variant="text" 
                size="small" 
                class="input-action-btn emoji-btn"
                @click="toggleEmojiPicker"
              >
                <v-icon size="20">fas fa-smile</v-icon>
              </v-btn>

              <!-- Botão anexo melhorado -->
              <v-btn 
                icon 
                variant="text" 
                size="small" 
                class="input-action-btn attachment-btn"
                @click="openFilePicker"
              >
                <v-icon size="20">fas fa-paperclip</v-icon>
              </v-btn>

              <!-- Container do input principal MELHORADO -->
              <div class="message-input-container enhanced-container">
                <textarea
                  ref="messageInput"
                  v-model="messageText"
                  placeholder="Digite uma mensagem..." 
                  class="message-input enhanced-textarea"
                  rows="1"
                  @focus="handleInputFocus"
                  @blur="handleInputBlur"
                  @input="handleInput"
                  @keydown="handleKeyDown"
                  @paste="handlePaste"
                />
                
                <!-- Contador de caracteres -->
                <div v-if="messageText.length > 3000" class="char-counter">
                  {{ messageText.length }}/4096
                </div>
              </div>

              <!-- Botão de enviar SUPER MELHORADO -->
              <v-btn 
                icon 
                variant="text" 
                size="small" 
                :class="['send-btn', 'enhanced-send', {
                  'active': messageText.trim(),
                  'sending': isSending
                }]"
                :disabled="isSending"
                @click="sendMessage"
              >
                <v-icon 
                  :size="messageText.trim() ? '18' : '20'" 
                  :class="{ 'spin': isSending }"
                >
                  {{ isSending ? 'fas fa-spinner' : (messageText.trim() ? 'fas fa-paper-plane' : 'fas fa-microphone') }}
                </v-icon>
                
                <!-- Efeito de onda no clique -->
                <div class="ripple-effect"></div>
              </v-btn>
            </div>
          </div>

          <!-- Tela inicial -->
          <div v-else class="welcome-screen">
            <div class="welcome-content">
              <v-icon size="200" color="#d9dee0">fa-solid fa-comment</v-icon>
              <h2 class="welcome-title">Sicon chat</h2>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Transfer Dialog -->
    <v-dialog v-model="transferDialog" max-width="500px" persistent>
      <v-card class="transfer-dialog">
        <div class="transfer-header">
          <h3>Transferir Conversa</h3>
          <v-btn icon variant="text" @click="transferDialog = false" size="small">
            <v-icon color="white">fas fa-times</v-icon>
          </v-btn>
        </div>

        <v-card-text class="pa-6">
          <div class="transfer-info mb-4">
            <div class="text-body-2 mb-2">
              <strong>Conversa:</strong> 
              {{ selectedConversation?.title || selectedConversation?.name || 'Conversa selecionada' }}
            </div>
            <div class="text-caption text-medium-emphasis">
              Esta ação irá transferir toda a conversa para outro usuário ou departamento.
            </div>
          </div>

          <v-form ref="transferForm" v-model="transferFormValid" lazy-validation>
            <div class="mb-4">
              <v-radio-group v-model="transferData.type" inline>
                <v-radio label="Transferir para usuário" value="user"></v-radio>
                <v-radio label="Transferir para departamento" value="department"></v-radio>
              </v-radio-group>
            </div>

            <v-select v-if="transferData.type === 'user'"
                      v-model="transferData.toUserId"
                      :items="availableUsers"
                      item-title="name"
                      item-value="id"
                      label="Selecione o usuário"
                      placeholder="Escolha um usuário..."
                      :loading="loadingUsers"
                      :rules="[v => !!v || 'Usuário é obrigatório']"
                      required>
              <template #prepend-item>
                <v-list-item>
                  <v-list-item-title class="text-caption text-medium-emphasis">
                    Usuários disponíveis
                  </v-list-item-title>
                </v-list-item>
                <v-divider></v-divider>
              </template>
            </v-select>

            <v-select v-else-if="transferData.type === 'department'"
                      v-model="transferData.departmentId"
                      :items="availableDepartments"
                      item-title="name"
                      item-value="id"
                      label="Selecione o departamento"
                      placeholder="Escolha um departamento..."
                      :loading="loadingDepartments"
                      :rules="[v => !!v || 'Departamento é obrigatório']"
                      required>
              <template #prepend-item>
                <v-list-item>
                  <v-list-item-title class="text-caption text-medium-emphasis">
                    Departamentos disponíveis
                  </v-list-item-title>
                </v-list-item>
                <v-divider></v-divider>
              </template>
            </v-select>

            <v-textarea v-model="transferData.notes"
                        label="Observações (opcional)"
                        placeholder="Adicione uma observação sobre a transferência..."
                        rows="3"
                        counter="500"
                        maxlength="500"
                        variant="outlined"
                        class="mt-4">
            </v-textarea>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="outlined" @click="transferDialog = false" :disabled="transferring">
            Cancelar
          </v-btn>
          <v-btn color="#25d366" :loading="transferring" @click="confirmTransfer">
            <v-icon left size="18">fas fa-exchange-alt</v-icon>
            Transferir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Transfer History Dialog -->
    <v-dialog v-model="showTransferHistory" max-width="700px">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Histórico de Transferências</span>
          <v-btn icon variant="text" @click="showTransferHistory = false">
            <v-icon>fas fa-times</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text>
          <div v-if="loadingTransferHistory" class="text-center pa-4">
            <v-progress-circular indeterminate color="#25d366"></v-progress-circular>
            <div class="mt-2">Carregando histórico...</div>
          </div>
          
          <div v-else-if="!transfers.length" class="text-center pa-8">
            <v-icon size="64" color="grey-lighten-1">fas fa-history</v-icon>
            <div class="text-h6 mt-4 text-medium-emphasis">Nenhuma transferência encontrada</div>
            <div class="text-body-2 text-medium-emphasis">Esta conversa ainda não foi transferida.</div>
          </div>
          
          <v-timeline v-else side="end" density="compact">
            <v-timeline-item v-for="transfer in transfers" 
                             :key="transfer.id"
                             size="small"
                             dot-color="#25d366">
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="text-body-1 font-weight-medium">
                  {{ getTransferOriginLabel(transfer) }} → {{ getTransferDestinationLabel(transfer) }}
                </div>
              </div>
              <div class="text-body-2 text-medium-emphasis mb-2">
                {{ formatDateTime(transfer.createdAt) }}
              </div>
              <div v-if="transfer.notes || transfer.transferMessage" class="text-body-2 text-medium-emphasis">
                <em>"{{ transfer.notes || transfer.transferMessage }}"</em>
              </div>
            </v-timeline-item>
          </v-timeline>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" 
                :color="snackbar.color" 
                :timeout="snackbar.timeout"
                location="top">
      {{ snackbar.message }}
      <template #actions>
        <v-btn variant="text" @click="snackbar.show = false">Fechar</v-btn>
      </template>
    </v-snackbar>
  </component>
</template>

<script>
import { io } from 'socket.io-client';
import axios from 'axios';
import { useAuthStore } from '@/stores/auth'

export default {
  name: 'SiconChat',
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      socket: null,
      currentUser: { 
        id: 1,
        name: 'Usuário Sicon', 
        avatar: null,
        departmentId: null
      },
      conversations: [],
      selectedConversation: null,
      auth: null,
      token: null,
      messages: [],
      messagesByConversation: Object.create(null),
      messageText: '',
      searchQuery: '',
      searchInMessages: '',
      showSearch: false,
      showScrollButton: false,
      isTyping: false,
      typingTimer: null,
      hasNewMessages: false,
      defaultAvatar: (name) => `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'User')}&background=075E54&color=fff`,
      apiUrl: (process.env.VUE_APP_API_URL || 'https://sicon-ia.cloud/api').replace(/\/$/, ''),
      whatsappInstance: String(process.env.VUE_APP_WHATSAPP_INSTANCE || 'sicon').trim() || 'sicon',
      socketUrl: (
        process.env.VUE_APP_SOCKET_URL
        || process.env.VUE_APP_API_URL
        || 'https://sicon-ia.cloud'
      ).replace(/\/$/, '').replace(/\/api$/, ''),

      selectedFilter: 'all',
      filters: [
        { label: 'Todas', value: 'all', count: 0 },
        { label: 'Não lidas', value: 'unread', count: 0 },
        { label: 'Grupos', value: 'groups', count: 0 },
        { label: 'Transferidas', value: 'transferred', count: 0 }
      ],
      loading: false,
      error: null,
      whatsappReady: null,
      qrDataUrl: null,
      joinedConversationId: null,
      pendingMessageAcks: Object.create(null),
      pendingMediaAcks: Object.create(null),
      lastHistoryAutoSyncAt: Object.create(null),
      lastProfilePicsSyncAt: 0,

      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 3000
      },
      messagesLoading: false,
      messagesError: null,

      transferDialog: false,
      transferFormValid: false,
      transferring: false,
      transferData: {
        type: 'user',
        toUserId: null,
        departmentId: null,
        notes: ''
      },
      availableUsers: [],
      availableDepartments: [],
      loadingUsers: false,
      loadingDepartments: false,
      transferMarks: [],
      showTransferHistory: false,
      loadingTransferHistory: false,
      audioPlayers: {},
      audioProgress: {},
      audioDurations: {},
      isSending: false,
      inputFocused: false,

      transfers: []
    };
  },

  computed: {
    filteredConversations() {
      let list = this.conversations;

      if (this.selectedFilter === 'unread') {
        list = list.filter(conv => Number(conv.unreadCount || 0) > 0);
      } else if (this.selectedFilter === 'groups') {
        list = list.filter(conv => Boolean(conv.isGroup));
      } else if (this.selectedFilter === 'transferred') {
        list = list.filter(conv => Boolean(conv.isTransferred));
      }

      if (!this.searchQuery) return list;
      const query = this.searchQuery.toLowerCase();
      return list.filter(conv =>
        (conv.title || '').toLowerCase().includes(query) ||
        (conv.lastMessage?.content || '').toLowerCase().includes(query)
      );
    },

    groupedMessages() {
      const groups = {};
      const filtered = this.searchInMessages 
        ? this.messages.filter(m => m.content?.toLowerCase().includes(this.searchInMessages.toLowerCase()))
        : this.messages;
      filtered.forEach(m => {
        const date = new Date(m.createdAt).toDateString();
        if (!groups[date]) groups[date] = [];
        groups[date].push(m);
      });
      return groups;
    }
  },

  watch: {
    showTransferHistory(isOpen) {
      if (isOpen) {
        this.loadTransferHistory();
      }
    },
    conversations: {
      deep: true,
      handler() {
        this.updateFilterCounts();
      }
    }
  },

  async mounted() {
    // inicializa o store AGORA que o componente está sendo montado (Pinia já registrado em main.ts)
    this.auth = useAuthStore();
    this.token = this.auth?.token || null;

    if (this.auth?.user) {
      this.currentUser = {
        id: this.auth.user.id ?? this.currentUser.id,
        name: this.auth.user.name || this.currentUser.name,
        avatar: this.auth.user.avatar || null,
        departmentId: this.auth.user.departmentId || this.auth.user.department?.id || null
      };
    }

    await this.loadCurrentUser();
    await this.loadTransferMarks();
    this.initializeSocket();
    this.loadUsers();
    this.loadDepartments();
  },

  methods: {
    convKey(id) {
      if (id === null || id === undefined) return '';
      return String(id).trim();
    },

    comparableConversationId(id) {
      const raw = this.convKey(id).toLowerCase();
      if (!raw) return '';

      const withoutDevice = raw.replace(/:\d+(?=@)/g, '');
      if (!withoutDevice.includes('@')) {
        if (withoutDevice.includes('-')) return `${withoutDevice}@g.us`;
        const digits = withoutDevice.replace(/\D/g, '');
        return digits.length >= 8 ? digits : withoutDevice;
      }

      const [userPart = '', serverPart = ''] = withoutDevice.split('@');
      const user = userPart.replace(/:\d+$/, '');

      if (serverPart === 's.whatsapp.net' || serverPart === 'c.us' || serverPart === 'lid') {
        const digits = user.replace(/\D/g, '');
        return digits || user;
      }

      if (serverPart === 'g.us') return `${user}@g.us`;
      return `${user}@${serverPart}`;
    },

    resolveExistingConversationId(candidateId) {
      const key = this.convKey(candidateId);
      if (!key) return '';

      const exact = this.conversations.find(conv => this.convKey(conv.id) === key);
      if (exact) return this.convKey(exact.id);

      const targetComparable = this.comparableConversationId(key);
      if (!targetComparable) return key;

      const comparableMatch = this.conversations.find(conv => (
        this.comparableConversationId(conv.id) === targetComparable
      ));

      return comparableMatch ? this.convKey(comparableMatch.id) : key;
    },

    updateFilterCounts() {
      const counters = {
        all: this.conversations.length,
        unread: this.conversations.filter(conv => Number(conv.unreadCount || 0) > 0).length,
        groups: this.conversations.filter(conv => Boolean(conv.isGroup)).length,
        transferred: this.conversations.filter(conv => Boolean(conv.isTransferred)).length
      };

      this.filters = this.filters.map(filter => ({
        ...filter,
        count: counters[filter.value] || 0
      }));
    },

    getAuthHeaders(extra = {}) {
      const headers = {};

      if (this.token) {
        headers.Authorization = `Bearer ${this.token}`;
      }

      if (this.currentUser?.id !== null && this.currentUser?.id !== undefined) {
        headers['x-user-id'] = String(this.currentUser.id);
      }

      if (this.currentUser?.departmentId) {
        headers['x-department-id'] = String(this.currentUser.departmentId);
      }

      return {
        ...headers,
        ...extra
      };
    },

    normalizeTransferMark(mark, fallbackConversationId = '') {
      if (!mark || typeof mark !== 'object') return null;

      const normalizedConversationId = this.convKey(
        mark.conversationId
        || mark.sourceConversationId
        || fallbackConversationId
      );

      if (!normalizedConversationId) return null;

      return {
        id: String(mark.id || `${normalizedConversationId}_${mark.createdAt || mark.updatedAt || 'mark'}`),
        conversationId: normalizedConversationId,
        sourceConversationId: this.convKey(mark.sourceConversationId || normalizedConversationId),
        departmentId: mark.departmentId ?? null,
        toUserId: mark.toUserId ?? null,
        fromUserId: mark.fromUserId ?? null,
        notes: mark.notes ?? null,
        createdAt: mark.createdAt || null,
        updatedAt: mark.updatedAt || null,
        toUser: mark.toUser || null,
        fromUser: mark.fromUser || null,
        department: mark.department || null
      };
    },

    upsertTransferMark(mark, fallbackConversationId = '') {
      const normalized = this.normalizeTransferMark(mark, fallbackConversationId);
      if (!normalized) return;

      const conversationId = this.convKey(normalized.conversationId);
      const comparableId = this.comparableConversationId(conversationId);

      const existingIndex = this.transferMarks.findIndex(item => (
        this.convKey(item.conversationId) === conversationId
        || this.comparableConversationId(item.conversationId) === comparableId
      ));

      if (existingIndex >= 0) {
        this.transferMarks.splice(existingIndex, 1, normalized);
      } else {
        this.transferMarks.push(normalized);
      }
    },

    hydrateTransferMarksFromPayload(payloadConversations = [], payloadTransferMarks = []) {
      const fromConversations = payloadConversations
        .map(conv => this.normalizeTransferMark(
          conv?.transferMark,
          conv?.id || conv?.remoteJid || conv?.conversationId
        ))
        .filter(Boolean);

      const fromPayloadMarks = payloadTransferMarks
        .map(mark => this.normalizeTransferMark(mark))
        .filter(Boolean);

      const merged = [...fromPayloadMarks, ...fromConversations];
      const uniqueMarks = [];

      merged.forEach(mark => {
        const key = this.convKey(mark.conversationId);
        const comparable = this.comparableConversationId(key);
        const exists = uniqueMarks.some(item => (
          this.convKey(item.conversationId) === key
          || this.comparableConversationId(item.conversationId) === comparable
        ));

        if (!exists) uniqueMarks.push(mark);
      });

      this.transferMarks = uniqueMarks;
    },

    getTransferOriginLabel(transfer) {
      if (transfer?.fromUser?.name) return transfer.fromUser.name;
      if (transfer?.fromUserId !== null && transfer?.fromUserId !== undefined) {
        return `Usuário ${transfer.fromUserId}`;
      }
      return 'Sistema';
    },

    getTransferDestinationLabel(transfer) {
      if (transfer?.toUser?.name) return transfer.toUser.name;
      if (transfer?.toUserId !== null && transfer?.toUserId !== undefined) {
        return `Usuário ${transfer.toUserId}`;
      }
      if (transfer?.department?.name) return transfer.department.name;
      if (transfer?.departmentId) {
        return `Departamento ${transfer.departmentId}`;
      }
      return 'Destino não informado';
    },

    getConversationTransferredTo(conversation) {
      if (!conversation) return 'outro destino';
      if (conversation.transferredTo) return conversation.transferredTo;
      return this.getTransferDestinationLabel(conversation.transferMark || conversation);
    },

    hasConversationAccessByTransferMark(conversationId) {
      const convId = this.convKey(conversationId);
      const convComparable = this.comparableConversationId(convId);
      const transferMark = this.transferMarks.find(tm => (
        this.convKey(tm.conversationId) === convId
        || this.comparableConversationId(tm.conversationId) === convComparable
      ));
      if (!transferMark) return true;

      if (transferMark.toUserId) {
        return String(transferMark.toUserId) === String(this.currentUser.id);
      }

      if (transferMark.departmentId) {
        return String(transferMark.departmentId) === String(this.currentUser.departmentId);
      }

      return true;
    },

    normalizeMessageStatus(status, fromMe = false) {
      if (!status) return fromMe ? 'SENT' : 'DELIVERED';
      const value = String(status).toUpperCase();
      if (value === 'RECEIVED') return 'DELIVERED';
      if (value === 'SENT' || value === 'SENDING' || value === 'DELIVERED' || value === 'READ' || value === 'ERROR') {
        return value;
      }
      return fromMe ? 'SENT' : 'DELIVERED';
    },

    isLikelyConversationId(value) {
      if (value === null || value === undefined) return false;
      const id = String(value).trim();
      if (!id) return false;
      if (id.includes('@')) return true;
      if (id.includes('-')) return true;
      const digits = id.replace(/\D/g, '');
      return digits.length >= 8;
    },

    extractConversationCandidates(message) {
      if (!message || typeof message !== 'object') return [];

      const key = message.key && typeof message.key === 'object' ? message.key : {};
      const data = message.data && typeof message.data === 'object' ? message.data : {};
      const nested = message.message && typeof message.message === 'object' ? message.message : {};

      const rawCandidates = [
        message.conversationId,
        message.remoteJid,
        message.contactId,
        message.chatId,
        message.chat?.id,
        message.chat?.jid,
        message.jid,
        key.remoteJid,
        key.participant,
        data.conversationId,
        data.remoteJid,
        data.contactId,
        nested.conversationId,
        nested.remoteJid,
        nested.contactId,
        nested.chatId,
        message.from,
        message.to
      ];

      return rawCandidates
        .filter(item => item !== null && item !== undefined)
        .filter(item => typeof item === 'string' || typeof item === 'number')
        .map(item => String(item).trim())
        .filter(Boolean);
    },

    unwrapIncomingPayload(message) {
      if (!message || typeof message !== 'object') return message;

      const nested = message.message;
      if (!nested || typeof nested !== 'object' || Array.isArray(nested)) {
        return message;
      }

      const key = message.key && typeof message.key === 'object' ? message.key : {};
      return {
        ...message,
        ...nested,
        key: Object.keys(key).length ? key : nested.key,
        message: nested,
        id: message.id || nested.id || key.id,
        messageId: message.messageId || nested.messageId || key.id,
        remoteJid: message.remoteJid
          || message.conversationId
          || message.contactId
          || nested.remoteJid
          || key.remoteJid,
        fromMe: typeof message.fromMe === 'boolean'
          ? message.fromMe
          : (typeof key.fromMe === 'boolean'
            ? key.fromMe
            : nested.fromMe),
        timestamp: message.timestamp
          || message.createdAt
          || message.messageTimestamp
          || nested.timestamp
          || nested.createdAt
          || nested.messageTimestamp
      };
    },

    resolveMessageContent(message) {
      if (!message || typeof message !== 'object') return '';

      const payload = this.unwrapIncomingPayload(message);
      const nested = payload?.message && typeof payload.message === 'object' ? payload.message : {};

      const candidates = [
        payload?.content,
        payload?.body,
        payload?.text,
        payload?.conversation,
        payload?.caption,
        nested?.conversation,
        nested?.extendedTextMessage?.text,
        nested?.imageMessage?.caption,
        nested?.videoMessage?.caption,
        nested?.documentMessage?.caption,
        nested?.buttonsResponseMessage?.selectedDisplayText,
        nested?.templateButtonReplyMessage?.selectedDisplayText,
        nested?.listResponseMessage?.title,
        nested?.reactionMessage?.text
      ];

      const found = candidates.find(value => (
        value !== null
        && value !== undefined
        && String(value).trim().length > 0
      ));

      return found ? String(found) : '';
    },

    resolveConversationIdFromRealtime(message) {
      if (!message || typeof message !== 'object') return '';

      const payload = this.unwrapIncomingPayload(message);
      const candidates = [
        ...this.extractConversationCandidates(payload),
        ...this.extractConversationCandidates(message)
      ];
      const rawId = candidates.find(candidate => this.isLikelyConversationId(candidate))
        || candidates[0]
        || '';

      return this.resolveExistingConversationId(rawId);
    },

    inferMessageType(message) {
      const payload = this.unwrapIncomingPayload(message);
      const declared = payload?.type || payload?.messageType;
      if (declared) return declared;

      if (payload?.imageMessage || payload?.message?.imageMessage) return 'image';
      if (payload?.videoMessage || payload?.message?.videoMessage) return 'video';
      if (payload?.documentMessage || payload?.message?.documentMessage) return 'document';
      if (payload?.stickerMessage || payload?.message?.stickerMessage) return 'sticker';

      const audioPayload = payload?.audioMessage || payload?.message?.audioMessage;
      if (audioPayload) return audioPayload?.ptt ? 'ptt' : 'audio';

      if (payload?.hasMedia || payload?.media) {
        const mimetype = String(payload?.media?.mimetype || '').toLowerCase();
        if (mimetype.includes('image')) return 'image';
        if (mimetype.includes('audio')) return payload?.type === 'ptt' ? 'ptt' : 'audio';
        if (mimetype.includes('video')) return 'video';
        return 'document';
      }

      return 'text';
    },

    normalizeIncomingMessage(rawMessage, forcedConversationId = null) {
      const payload = this.unwrapIncomingPayload(rawMessage);
      const fromMe = Boolean(
        typeof payload?.fromMe === 'boolean'
          ? payload.fromMe
          : (typeof payload?.key?.fromMe === 'boolean'
            ? payload.key.fromMe
            : rawMessage?.fromMe)
      );
      const conversationId = this.resolveExistingConversationId(
        forcedConversationId
        || this.resolveConversationIdFromRealtime(payload)
        || this.resolveConversationIdFromRealtime(rawMessage)
        || this.selectedConversation?.id
      );
      const timestampInput = payload?.timestamp
        || payload?.createdAt
        || payload?.messageTimestamp
        || rawMessage?.timestamp
        || rawMessage?.createdAt
        || rawMessage?.messageTimestamp
        || Date.now();
      const content = this.resolveMessageContent(payload) || this.resolveMessageContent(rawMessage);
      const normalizedType = this.inferMessageType(payload);

      return {
        id: payload?.id
          || payload?.messageId
          || payload?.key?.id
          || rawMessage?.id
          || rawMessage?.messageId
          || rawMessage?.key?.id
          || `msg_${Date.now()}_${Math.random().toString(16).slice(2)}`,
        senderId: payload?.from
          || payload?.senderId
          || payload?.author
          || payload?.participant
          || payload?.key?.participant
          || payload?.key?.remoteJid
          || rawMessage?.from
          || 'unknown',
        content,
        createdAt: this.normalizeTimestamp(timestampInput),
        timestamp: this.normalizeTimestamp(timestampInput),
        avatar: this.normalizeAvatarUrl(
          payload?.profilePicUrl
          || payload?.profilePicture
          || rawMessage?.profilePicUrl
          || rawMessage?.profilePicture
          || null
        ),
        status: this.normalizeMessageStatus(payload?.status || rawMessage?.status, fromMe),
        type: normalizedType,
        fromMe,
        hasMedia: Boolean(payload?.hasMedia || payload?.media || rawMessage?.hasMedia || rawMessage?.media),
        media: payload?.media || rawMessage?.media || null,
        conversationId
      };
    },

    mapConversationSummary(rawConversation) {
      const conversationId = this.convKey(rawConversation?.id || rawConversation?.conversationId || rawConversation?.remoteJid || '');
      const displayName = rawConversation?.name || rawConversation?.title || this.cleanContactName(conversationId);
      const timestampInput = rawConversation?.lastMessageTime || rawConversation?.timestamp || rawConversation?.updatedAt || Date.now();
      const lastMessageText = typeof rawConversation?.lastMessage === 'string'
        ? rawConversation.lastMessage
        : (rawConversation?.lastMessage?.content || rawConversation?.lastMessage?.body || null);
      const normalizedTimestamp = this.normalizeTimestamp(timestampInput);
      const transferMark = this.normalizeTransferMark(
        rawConversation?.transferMark,
        rawConversation?.remoteJid || rawConversation?.id || rawConversation?.conversationId
      ) || this.normalizeTransferMark(
        this.transferMarks.find(tm => (
          this.convKey(tm?.conversationId) === conversationId
          || this.comparableConversationId(tm?.conversationId) === this.comparableConversationId(conversationId)
        )),
        conversationId
      );
      const isTransferred = Boolean(rawConversation?.isTransferred || transferMark);
      const transferredTo = rawConversation?.transferredTo || this.getTransferDestinationLabel(
        transferMark || {
          toUserId: rawConversation?.toUserId ?? null,
          departmentId: rawConversation?.departmentId ?? null
        }
      );
      const transferredFrom = rawConversation?.transferredFrom || this.getTransferOriginLabel(
        transferMark || {
          fromUserId: rawConversation?.fromUserId ?? null
        }
      );
      const transferMessage = rawConversation?.transferMessage || transferMark?.notes || null;

      return {
        id: conversationId,
        title: displayName,
        name: displayName,
        isGroup: Boolean(rawConversation?.isGroup),
        lastMessage: lastMessageText ? {
          id: `summary_${conversationId}`,
          content: lastMessageText,
          createdAt: normalizedTimestamp,
          timestamp: normalizedTimestamp,
          type: this.inferMessageType(rawConversation?.lastMessage || {}),
          hasMedia: Boolean(rawConversation?.lastMessage?.hasMedia || rawConversation?.lastMessage?.media),
          media: rawConversation?.lastMessage?.media || null,
          fromMe: Boolean(rawConversation?.fromMe),
          status: this.normalizeMessageStatus(rawConversation?.lastMessageStatus, Boolean(rawConversation?.fromMe))
        } : null,
        unreadCount: Number(rawConversation?.unreadCount || 0),
        avatar: this.normalizeAvatarUrl(
          rawConversation?.profilePicUrl || rawConversation?.profilePicture || rawConversation?.avatar,
          displayName
        ),
        phone: conversationId,
        isOnline: false,
        isMuted: false,
        isPinned: false,
        isTransferred,
        transferredTo: isTransferred ? transferredTo : null,
        transferredFrom: isTransferred ? transferredFrom : null,
        transferMessage,
        transferMark,
        department: rawConversation?.department || null,
        timestamp: normalizedTimestamp
      };
    },

    toEpoch(value) {
      if (!value) return 0;
      const date = new Date(value);
      const time = date.getTime();
      return Number.isFinite(time) ? time : 0;
    },

    maybeSyncSelectedConversationHistory(currentConversation, previousConversation = null, options = {}) {
      const { force = false } = options;
      if (!currentConversation || !this.socket?.connected) return;

      const selectedId = this.convKey(this.selectedConversation?.id);
      const conversationId = this.convKey(currentConversation.id);
      if (!selectedId || selectedId !== conversationId) return;

      const now = Date.now();
      const lastAutoSync = Number(this.lastHistoryAutoSyncAt[conversationId] || 0);
      if (now - lastAutoSync < 1200) return;

      const previousUnread = Number(previousConversation?.unreadCount || 0);
      const currentUnread = Number(currentConversation?.unreadCount || 0);
      const unreadIncreased = currentUnread > previousUnread;

      const previousSummaryTs = this.toEpoch(previousConversation?.lastMessage?.createdAt || previousConversation?.timestamp);
      const currentSummaryTs = this.toEpoch(currentConversation?.lastMessage?.createdAt || currentConversation?.timestamp);
      const summaryMovedForward = currentSummaryTs > previousSummaryTs;

      const localMessages = this.getConversationMessages(conversationId);
      const localLastMessage = localMessages[localMessages.length - 1];
      const localLastTs = this.toEpoch(localLastMessage?.createdAt || localLastMessage?.timestamp);
      const summaryAheadOfLocal = currentSummaryTs > localLastTs;

      const summaryContent = String(currentConversation?.lastMessage?.content || '').trim();
      const localLastContent = String(localLastMessage?.content || '').trim();
      const summaryContentChanged = Boolean(summaryContent) && summaryContent !== localLastContent;

      const shouldSync = force
        || unreadIncreased
        || summaryMovedForward
        || summaryAheadOfLocal
        || summaryContentChanged;

      if (!shouldSync) {
        return;
      }

      this.lastHistoryAutoSyncAt[conversationId] = now;
      this.fetchChatHistory(conversationId, false);
    },

    resolveConversationByAnyId(candidateId) {
      const key = this.convKey(candidateId);
      if (!key) return null;

      const resolvedId = this.resolveExistingConversationId(key) || key;
      const targetComparable = this.comparableConversationId(key);

      return this.conversations.find(conv => (
        this.convKey(conv.id) === resolvedId
        || this.convKey(conv.id) === key
        || (targetComparable && this.comparableConversationId(conv.id) === targetComparable)
      )) || null;
    },

    requestProfilePicturesSync(conversations = this.conversations) {
      if (!this.socket?.connected) return;

      const now = Date.now();
      if (now - Number(this.lastProfilePicsSyncAt || 0) < 8000) return;

      const conversationIds = conversations
        .map(conv => this.convKey(conv?.id))
        .filter(Boolean)
        .slice(0, 80);

      if (!conversationIds.length) return;

      this.lastProfilePicsSyncAt = now;

      this.socket.emit('whatsapp:get-profile-pics', conversationIds);
      this.socket.emit('whatsapp:get-profile-pics', { contactIds: conversationIds });
      this.socket.emit('whatsapp:get-profile-pics', { conversationIds });

      conversationIds.slice(0, 30).forEach(conversationId => {
        this.socket.emit('whatsapp:get-profile-pic', conversationId);
        this.socket.emit('whatsapp:get-profile-pic', { contactId: conversationId });
      });
    },

    hydrateConversationFromList(payload) {
      const previousByConversationId = new Map(
        this.conversations.map(conv => [this.convKey(conv.id), conv])
      );

      const convs = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.chats)
          ? payload.chats
        : Array.isArray(payload?.conversations)
          ? payload.conversations
          : [];

      const payloadTransferMarks = Array.isArray(payload?.transferMarks) ? payload.transferMarks : [];
      this.hydrateTransferMarksFromPayload(convs, payloadTransferMarks);

      this.conversations = convs
        .map(conv => this.mapConversationSummary(conv))
        .filter(conv => Boolean(conv.id))
        .filter(conv => this.hasConversationAccessByTransferMark(conv.id));

      if (this.selectedConversation) {
        const selectedId = this.convKey(this.selectedConversation.id);
        const selectedComparable = this.comparableConversationId(selectedId);
        const current = this.conversations.find(conv => (
          this.convKey(conv.id) === selectedId
          || this.comparableConversationId(conv.id) === selectedComparable
        ));
        if (current) {
          this.selectedConversation = current;
        } else {
          if (this.joinedConversationId) this.leaveConversationRoom(this.joinedConversationId);
          this.selectedConversation = null;
          this.messages = [];
          this.joinedConversationId = null;
        }
      }

      this.sortConversations();
      this.loading = false;
      this.error = null;
      this.updateFilterCounts();
      this.requestProfilePicturesSync(this.conversations);

      if (this.selectedConversation) {
        const selectedId = this.convKey(this.selectedConversation.id);
        const previousSelectedConversation = previousByConversationId.get(selectedId) || null;
        this.maybeSyncSelectedConversationHistory(
          this.selectedConversation,
          previousSelectedConversation,
          { force: true }
        );
      }
    },

    getConversationMessages(conversationId) {
      const key = this.resolveExistingConversationId(conversationId) || this.convKey(conversationId);
      return this.messagesByConversation[key] || [];
    },

    setConversationMessages(conversationId, msgs) {
      const key = this.resolveExistingConversationId(conversationId) || this.convKey(conversationId);
      this.messagesByConversation[key] = msgs;
    },

    appendMessage(conversationId, msg, options = {}) {
      const { autoScroll = true } = options;
      const key = this.resolveExistingConversationId(conversationId) || this.convKey(conversationId);
      if (!this.messagesByConversation[key]) this.messagesByConversation[key] = [];

      const exists = this.messagesByConversation[key].some(existing => String(existing.id) === String(msg.id));
      if (!exists) {
        this.messagesByConversation[key].push(msg);
      }

      if (this.convKey(this.selectedConversation?.id) === key) {
        this.messages = this.messagesByConversation[key];
        if (autoScroll) {
          this.$nextTick(() => this.scrollToBottom());
        }
      }
    },

    updateConversationWithMessage(conversationId, msgObj, rawMessage = {}) {
      const convId = this.resolveExistingConversationId(conversationId) || this.convKey(conversationId);
      let conv = this.conversations.find(c => this.convKey(c.id) === convId);

      if (!conv && !this.hasConversationAccessByTransferMark(convId)) {
        return null;
      }

      const contactName = rawMessage?.pushName
        || rawMessage?.pushname
        || rawMessage?.notifyName
        || rawMessage?.name
        || this.cleanContactName(convId);

      if (!conv) {
        conv = {
          id: convId,
          title: contactName,
          name: contactName,
          isGroup: Boolean(rawMessage?.isGroup || convId.includes('@g.us')),
          lastMessage: null,
          unreadCount: 0,
          avatar: this.normalizeAvatarUrl(rawMessage?.profilePicUrl || rawMessage?.profilePicture || null, contactName),
          phone: convId,
          isOnline: false,
          isMuted: false,
          isPinned: false,
          isTransferred: false,
          transferredTo: null,
          transferredFrom: null,
          department: null,
          timestamp: this.normalizeTimestamp(Date.now())
        };
        this.conversations.unshift(conv);
      }

      if (rawMessage?.profilePicUrl || rawMessage?.profilePicture) {
        conv.avatar = this.normalizeAvatarUrl(rawMessage.profilePicUrl || rawMessage.profilePicture, contactName);
      }

      conv.lastMessage = { ...msgObj };
      conv.timestamp = msgObj.timestamp;

      if (!msgObj.fromMe && this.convKey(this.selectedConversation?.id) !== convId) {
        conv.unreadCount = Number(conv.unreadCount || 0) + 1;
      }

      this.sortConversations();
      return conv;
    },

    queuePendingAck(kind, conversationId, tempMessageId) {
      const key = this.convKey(conversationId);
      const store = kind === 'media' ? this.pendingMediaAcks : this.pendingMessageAcks;
      if (!store[key]) store[key] = [];
      store[key].push(tempMessageId);
    },

    consumePendingAck(kind, conversationId) {
      const key = this.convKey(conversationId);
      const store = kind === 'media' ? this.pendingMediaAcks : this.pendingMessageAcks;
      if (!store[key]?.length) return null;
      const tempId = store[key].shift();
      if (!store[key].length) delete store[key];
      return tempId;
    },

    resolveConversationIdFromSendResult(payload) {
      if (payload?.conversationId) return this.resolveExistingConversationId(payload.conversationId);
      if (payload?.to) return this.resolveExistingConversationId(payload.to);
      if (this.selectedConversation?.id) return this.convKey(this.selectedConversation.id);
      return '';
    },

    applySendResult(kind, payload) {
      const conversationId = this.resolveConversationIdFromSendResult(payload);
      let tempId = conversationId ? this.consumePendingAck(kind, conversationId) : null;

      if (!tempId && this.selectedConversation?.id) {
        tempId = this.consumePendingAck(kind, this.selectedConversation.id);
      }

      if (!tempId) {
        if (payload?.success === false) {
          this.showSnackbar(payload?.error || 'Não foi possível enviar a mensagem.', 'error');
        }
        this.isSending = false;
        return;
      }

      const targetConversationId = conversationId || this.selectedConversation?.id;
      const messages = this.getConversationMessages(targetConversationId);
      const msg = messages.find(item => String(item.id) === String(tempId));

      if (msg) {
        msg.status = payload?.success === false ? 'ERROR' : 'SENT';
        if (payload?.timestamp) {
          msg.createdAt = this.normalizeTimestamp(payload.timestamp);
          msg.timestamp = this.normalizeTimestamp(payload.timestamp);
        }
      }

      const conv = this.conversations.find(item => this.convKey(item.id) === this.convKey(targetConversationId));
      if (conv && msg) {
        conv.lastMessage = { ...msg };
      }

      if (payload?.success === false) {
        this.showSnackbar(payload?.error || 'Não foi possível enviar a mensagem.', 'error');
      }

      this.isSending = false;
    },

    reconcileSentMessageWithTemp(msgObj) {
      if (!msgObj?.fromMe || !msgObj?.conversationId) return false;

      const kind = msgObj.hasMedia ? 'media' : 'message';
      const tempId = this.consumePendingAck(kind, msgObj.conversationId);
      const messages = this.getConversationMessages(msgObj.conversationId);
      let index = -1;

      if (tempId) {
        index = messages.findIndex(item => String(item.id) === String(tempId));
      } else {
        for (let i = messages.length - 1; i >= 0; i -= 1) {
          const item = messages[i];
          if (!item?.fromMe || !String(item.id).startsWith('temp_')) continue;
          if (item.type !== msgObj.type) continue;
          if (!msgObj.hasMedia && item.content !== msgObj.content) continue;
          index = i;
          break;
        }
      }

      if (index < 0) return false;

      const merged = {
        ...messages[index],
        ...msgObj,
        status: this.normalizeMessageStatus(msgObj.status, true)
      };

      messages.splice(index, 1, merged);
      if (this.convKey(this.selectedConversation?.id) === this.convKey(msgObj.conversationId)) {
        this.messages = messages;
      }

      this.updateConversationWithMessage(msgObj.conversationId, merged, { fromMe: true });
      this.isSending = false;
      return true;
    },

    handleFileSelect(event) {
      const files = Array.from(event?.target?.files || []);
      if (!files.length) return;

      files.forEach(file => {
        this.sendFileMessage(file);
      });

      event.target.value = '';
    },

    async sendFileMessage(file) {
      if (!this.selectedConversation || !file) return;

      const conversationId = this.convKey(this.selectedConversation.id);
      const maxSize = 16 * 1024 * 1024;
      if (file.size > maxSize) {
        this.showSnackbar('Arquivo muito grande. Máximo 16MB.', 'error');
        return;
      }

      let messageType = 'document';
      if (file.type.startsWith('image/')) messageType = 'image';
      else if (file.type.startsWith('video/')) messageType = 'video';
      else if (file.type.startsWith('audio/')) messageType = 'audio';

      let base64Data = '';
      try {
        base64Data = await this.fileToBase64(file);
      } catch (error) {
        console.error('Erro ao processar arquivo:', error);
        this.showSnackbar('Erro ao processar arquivo', 'error');
        return;
      }

      const caption = (this.messageText || '').trim();
      if (caption) this.messageText = '';

      const tempMessage = {
        id: `temp_media_${Date.now()}_${Math.random().toString(16).slice(2)}`,
        senderId: this.currentUser.id,
        content: caption,
        createdAt: this.normalizeTimestamp(Date.now()),
        timestamp: this.normalizeTimestamp(Date.now()),
        status: 'SENDING',
        type: messageType,
        fromMe: true,
        hasMedia: true,
        media: {
          data: base64Data,
          mimetype: file.type,
          filename: file.name,
          filesize: file.size
        },
        conversationId
      };

      this.appendMessage(conversationId, tempMessage);
      this.updateConversationWithMessage(conversationId, tempMessage, { fromMe: true });

      if (!this.socket?.connected) {
        tempMessage.status = 'ERROR';
        this.showSnackbar('Socket desconectado. Não foi possível enviar o arquivo.', 'error');
        return;
      }

      this.queuePendingAck('media', conversationId, tempMessage.id);

      this.socket.emit('whatsapp:send-media', {
        to: conversationId,
        conversationId,
        base64Data,
        mimetype: file.type,
        filename: file.name,
        caption
      });
    },

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const value = String(reader.result || '');
          const base64 = value.includes(',') ? value.split(',')[1] : value;
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    getLastMessagePreview(conv) {
  if (!conv.lastMessage) return 'Sem mensagens';
  
  if (conv.lastMessage.hasMedia) {
    switch (conv.lastMessage.type) {
      case 'image': return '📷 Imagem';
      case 'audio':
      case 'ptt': return '🎵 Áudio';
      case 'video': return '📹 Vídeo';
      case 'document': return '📄 Documento';
      case 'sticker': return '🎭 Figurinha';
      default: return '📎 Mídia';
    }
  }
  
  return conv.lastMessage.content || 'Sem mensagens';
},

    getMediaUrl(message) {
      if (!message.media) return '';

      if (message.media.data) {
        const mimetype = message.media.mimetype || 'application/octet-stream';
        return `data:${mimetype};base64,${message.media.data}`;
      }

      if (message.media.fileUrl) {
        const fileUrl = String(message.media.fileUrl);
        if (fileUrl.startsWith('http') || fileUrl.startsWith('//') || fileUrl.startsWith('data:')) {
          return fileUrl;
        }
        return `${this.apiUrl}/${fileUrl.replace(/^\//, '')}`;
      }

      return '';
    },

toggleAudio(messageId) {
  const audioElement = this.$refs[`audio-${messageId}`];
  if (!audioElement || !audioElement[0]) return;

  const audio = audioElement[0];

  if (this.isPlaying(messageId)) {
    audio.pause();
    this.audioPlayers[messageId] = false;
  } else {
    // Pausar outros áudios
    Object.keys(this.audioPlayers).forEach(id => {
      if (id !== messageId && this.audioPlayers[id]) {
        const otherAudio = this.$refs[`audio-${id}`];
        if (otherAudio && otherAudio[0]) {
          otherAudio[0].pause();
          this.audioPlayers[id] = false;
        }
      }
    });

    audio.play();
    this.audioPlayers[messageId] = true;
  }
},


isPlaying(messageId) {
  return this.audioPlayers[messageId] || false;
},

audioEnded(messageId) {
  this.audioPlayers[messageId] = false;
  this.audioProgress[messageId] = 0;
},


audioLoaded(messageId, event) {
  const duration = event.target.duration;
  this.audioDurations[messageId] = duration; // já reativo no Vue 3

  // Atualizar progresso durante a reprodução
  event.target.addEventListener('timeupdate', () => {
    if (this.isPlaying(messageId)) {
      const progress = (event.target.currentTime / duration) * 100;
      this.audioProgress[messageId] = progress; // também reativo
    }
  });
},


getAudioProgress(messageId) {
  return this.audioProgress[messageId] || 0;
},

getAudioDuration(message) {
  const messageId = message.id;
  
  if (message.media && message.media.duration) {
    return this.formatAudioDuration(parseInt(message.media.duration));
  }
  
  if (this.audioDurations[messageId]) {
    return this.formatAudioDuration(this.audioDurations[messageId]);
  }
  
  return '0:00';
},

formatAudioDuration(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
},

openImageViewer(message) {
  const imageUrl = this.getMediaUrl(message);
  if (imageUrl) {
    window.open(imageUrl, '_blank');
  }
},

handleImageError(event) {
  console.warn('Erro ao carregar imagem:', event);
  event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTIxIDMuNVY2TDE5IDRIMTMuNUwxNSA1LjVIMTlWMTlIMTlWMjBMMjEgMTguNVYzLjVaIiBmaWxsPSIjOTk5Ii8+CjxwYXRoIGQ9Ik0zIDZWMjBIMTdWNkgzWk01IDhIMTVWMThINVY4WiIgZmlsbD0iIzk5OSIvPgo8L3N2Zz4K';
},

getFileSize(data) {
  if (!data) return '0 KB';
  const bytes = data.length * 0.75; // Aproximação do tamanho base64
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
},

// 4. Adicionar handlers para o input (adicionar no methods):
handleInputFocus() {
  this.inputFocused = true;
},

handleInputBlur() {
  this.inputFocused = false;
},

handleInput(event) {
  // Auto-resize do textarea
  const textarea = event.target;
  textarea.style.height = 'auto';
  textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  
  // Indicador de digitação
  if (this.typingTimer) clearTimeout(this.typingTimer);
  this.isTyping = true;
  
  this.typingTimer = setTimeout(() => {
    this.isTyping = false;
  }, 1000);
},

handleKeyDown(event) {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    this.sendMessage();
  }
},

handlePaste(event) {
  // Aqui você pode implementar upload de arquivos via paste
  console.log('Arquivo colado:', event);
},

toggleEmojiPicker() {
  // Implementar seletor de emoji
  console.log('Abrir seletor de emoji');
},

    openFilePicker() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },
    normalizeTimestamp(ts) {
      if (!ts) return new Date().toISOString();
      let date = typeof ts === 'number' ? new Date(ts < 1e12 ? ts * 1000 : ts) : new Date(ts);
      return isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
    },

    generateAvatar(name, profilePicUrl = null) {
  if (profilePicUrl) return this.normalizeAvatarUrl(profilePicUrl, name || 'User');
  const displayName = name || "User";
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=075E54&color=fff`;
},


    // normaliza URLs de avatar: mantém data: e http(s), e prefixa apiUrl quando for caminho relativo
normalizeAvatarUrl(url, fallbackName = 'User') {
  if (!url) {
    // fallback para avatar gerado
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(fallbackName)}&background=075E54&color=fff`;
  }

  // já está em data: ou é URL absoluta
  if (typeof url === 'string' && (url.startsWith('data:') || url.startsWith('http') || url.startsWith('//'))) {
    return url;
  }

  // se for provavelmente base64 sem prefixo (apenas caracteres base64), prefixar como jpeg
  if (typeof url === 'string' && /^[A-Za-z0-9+/=]+\s*$/.test(url) && url.length > 100) {
    return `data:image/jpeg;base64,${url}`;
  }

  // caminho relativo -> prefixa apiUrl
  const base = (this.apiUrl || '').replace(/\/$/, '');
  return `${base}/${String(url).replace(/^\//, '')}`;
},


    async loadCurrentUser() {
      try {
        const response = await axios.get(`${this.apiUrl}/me`, {
          headers: this.getAuthHeaders()
        });
        this.currentUser = {
          id: response.data.id ?? this.currentUser.id,
          name: response.data.name || this.currentUser.name,
          avatar: response.data.avatar || null,
          departmentId: response.data.departmentId || response.data.department?.id || null
        };
      } catch (error) {
        console.error('Erro ao carregar usuário atual:', error);
        if (this.auth?.user) {
          this.currentUser = {
            id: this.auth.user.id ?? this.currentUser.id,
            name: this.auth.user.name || this.currentUser.name,
            avatar: this.auth.user.avatar || null,
            departmentId: this.auth.user.departmentId || this.auth.user.department?.id || null
          };
        }
      }
    },

    initializeSocket() {
      this.socket = io(this.socketUrl, {
        path: '/socket.io',
        transports: ['websocket', 'polling'],
        withCredentials: true,
        auth: this.token ? { token: this.token } : undefined,
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000
      });

      this.socket.onAny((event, payload) => {
        console.log('[SOCKET]', event, payload);
      });

      this.socket.on('connect', () => {
        console.log('Sicon Chat conectado', this.socket.id);
        this.socket.emit('whatsapp:check-status');
        this.fetchChats();

        const activeConversationId = this.convKey(this.selectedConversation?.id);
        if (activeConversationId) {
          this.socket.emit('join:conversation', { conversationId: activeConversationId });
          this.socket.emit('whatsapp:fetch-chat-history', activeConversationId);
          this.socket.emit('whatsapp:fetch-chat-history', { conversationId: activeConversationId });
          this.joinedConversationId = activeConversationId;
          this.messagesLoading = true;
          this.messagesError = null;
        }
      });

      this.socket.on('connect_error', error => {
        console.error('Erro ao conectar socket do chat:', error?.message || error);
      });

      this.socket.on('whatsapp:status', payload => {
        this.whatsappReady = typeof payload?.ready === 'boolean' ? payload.ready : null;
        if (this.whatsappReady) {
          this.fetchChats();
        }
      });

      this.socket.on('whatsapp:qr', payload => {
        this.qrDataUrl = payload?.dataUrl || null;
      });

      this.socket.on('whatsapp:ready', () => {
        this.whatsappReady = true;
        this.qrDataUrl = null;
        this.fetchChats();
      });

      this.socket.on('whatsapp:authenticated', () => {
        this.whatsappReady = true;
      });

      this.socket.on('whatsapp:auth_failure', () => {
        this.whatsappReady = false;
        this.showSnackbar('Falha de autenticação do WhatsApp.', 'error');
      });

      this.socket.on('whatsapp:disconnected', () => {
        this.whatsappReady = false;
      });

      this.socket.on('whatsapp:error', payload => {
        const message = payload?.message || 'Erro de comunicação com WhatsApp.';
        if (this.loading) {
          this.error = message;
          this.loading = false;
        }

        if (this.messagesLoading) {
          this.messagesLoading = false;
          this.messagesError = message;
        }

        this.showSnackbar(message, 'error');
      });

      this.socket.on('whatsapp:conversations', () => {
        this.fetchChats();
      });

      this.socket.on('whatsapp:chat-history', payload => {
        const payloadData = Array.isArray(payload) ? { messages: payload } : (payload || {});
        const fallbackConversationId = this.convKey(this.selectedConversation?.id || '');
        const conversationId = this.resolveExistingConversationId(
          payloadData.conversationId || payloadData.remoteJid || payloadData.contactId || fallbackConversationId
        );
        if (!conversationId) {
          this.messagesLoading = false;
          return;
        }

        const history = Array.isArray(payloadData.messages)
          ? payloadData.messages
          : (Array.isArray(payloadData.history) ? payloadData.history : []);
        const normalizedMessages = history.map(message => this.normalizeIncomingMessage(message, conversationId));
        this.setConversationMessages(conversationId, normalizedMessages);

        if (this.convKey(this.selectedConversation?.id) === conversationId) {
          this.messages = normalizedMessages;
          this.messagesLoading = false;
          this.messagesError = null;
          this.$nextTick(() => this.scrollToBottom());
        }

        const last = normalizedMessages[normalizedMessages.length - 1];
        if (last) {
          this.updateConversationWithMessage(conversationId, last, { fromMe: last.fromMe });
        }
      });

      this.socket.on('whatsapp:profile-pic', payload => {
        const contactId = payload?.contactId || payload?.conversationId || payload?.remoteJid || '';
        const conv = this.resolveConversationByAnyId(contactId);
        if (conv) {
          conv.avatar = this.normalizeAvatarUrl(payload?.profilePicUrl, conv.name || conv.title || 'User');
        }
      });

      this.socket.on('whatsapp:profile-pics', payload => {
        const profilePics = payload?.profilePics || {};
        Object.entries(profilePics).forEach(([contactId, picUrl]) => {
          const conv = this.resolveConversationByAnyId(contactId);
          if (conv) {
            conv.avatar = this.normalizeAvatarUrl(picUrl, conv.name || conv.title || 'User');
          }
        });
      });

      this.socket.on('whatsapp:contact-info', payload => {
        const contactId = payload?.contactId || payload?.conversationId || payload?.remoteJid || '';
        const conv = this.resolveConversationByAnyId(contactId);
        if (!conv) return;

        const info = payload?.contactInfo || {};
        conv.name = info.name || info.pushname || conv.name;
        conv.title = info.name || info.pushname || conv.title;
        conv.isGroup = Boolean(info.isGroup);
        conv.avatar = this.normalizeAvatarUrl(info.profilePicUrl, conv.name || conv.title || 'User');
      });

      this.socket.on('whatsapp:send-message:result', payload => {
        this.applySendResult('message', payload);
      });

      this.socket.on('whatsapp:send-media:result', payload => {
        this.applySendResult('media', payload);
      });

      this.socket.on('whatsapp:message', msg => this.handleNewMessage(msg));
      this.socket.on('whatsapp:new-message', msg => this.handleNewMessage(msg));
      this.socket.on('whatsapp:sent-message', msg => this.handleNewMessage(msg));
      this.socket.on('new_message', msg => this.handleNewMessage(msg));
      this.socket.on('sent_message', msg => this.handleNewMessage(msg));
      this.socket.on('message:new', payload => this.handleNewMessage(payload));

      this.socket.on('conversation:transfer-marked', data => {
        const conversationId = this.convKey(data?.conversationId || '');
        const transferMark = data?.transferMark;
        if (!conversationId || !transferMark) return;

        this.upsertTransferMark(transferMark, conversationId);
        this.fetchChats(true);
      });

      this.socket.on('conversation:transfer-mark-removed', () => {
        this.fetchChats(true);
      });

      this.socket.on('conversation:transfer-unmarked', () => {
        this.fetchChats(true);
      });

      this.socket.on('conversation:transferred', transferData => {
        this.handleConversationTransferred(transferData);
      });

      this.socket.on('transfer:status-updated', transferData => {
        this.handleTransferStatusUpdate(transferData);
      });
    },

    async fetchChats(forceRefresh = false) {
      this.loading = true;
      this.error = null;

      const instance = this.whatsappInstance || 'sicon';
      const headers = this.getAuthHeaders();

      try {
        let payload = null;

        try {
          const response = await axios.get(`${this.apiUrl}/findChats/${encodeURIComponent(instance)}`, {
            headers
          });
          payload = response.data;
        } catch (findChatsError) {
          const status = findChatsError?.response?.status;
          const shouldFallback = status === 404 || status === 405;
          if (!shouldFallback) throw findChatsError;

          const fallbackResponse = await axios.get(`${this.apiUrl}/whatsapp/conversations`, {
            headers,
            params: {
              ...(this.currentUser?.id ? { userId: this.currentUser.id } : {}),
              ...(this.currentUser?.departmentId ? { departmentId: this.currentUser.departmentId } : {})
            }
          });
          payload = fallbackResponse.data;
        }

        this.hydrateConversationFromList(payload);

        if (forceRefresh && this.socket?.connected) {
          this.socket.emit('whatsapp:refresh-conversations');
        }
      } catch (error) {
        console.error('Erro ao carregar conversas:', error);
        this.loading = false;
        this.error = error?.response?.data?.error
          || error?.response?.data?.message
          || 'Não foi possível carregar as conversas.';
      }
    },

    fetchChatHistory(conversationId, showLoading = true) {
      if (!this.socket?.connected) {
        this.messagesLoading = false;
        this.messagesError = 'Socket desconectado. Não foi possível carregar o histórico.';
        return;
      }
      const convId = this.resolveExistingConversationId(conversationId) || this.convKey(conversationId);
      if (!convId) return;

      if (showLoading) this.messagesLoading = true;
      this.messagesError = null;
      this.socket.emit('whatsapp:fetch-chat-history', convId);
      this.socket.emit('whatsapp:fetch-chat-history', { conversationId: convId });
    },

    joinConversationRoom(conversationId) {
      if (!this.socket?.connected || !conversationId) return;
      const convId = this.resolveExistingConversationId(conversationId) || this.convKey(conversationId);
      this.socket.emit('join:conversation', { conversationId: convId });
      // Fallback para servidores que aceitam payload direto em string.
      this.socket.emit('join:conversation', convId);
      this.joinedConversationId = convId;
    },

    leaveConversationRoom(conversationId) {
      if (!this.socket?.connected || !conversationId) return;
      const convId = this.resolveExistingConversationId(conversationId) || this.convKey(conversationId);
      this.socket.emit('leave:conversation', { conversationId: convId });
      this.socket.emit('leave:conversation', convId);
      if (this.joinedConversationId === convId) {
        this.joinedConversationId = null;
      }
    },

    async loadTransferMarks() {
      try {
        const response = await axios.get(`${this.apiUrl}/transfer-marks`, {
          headers: this.getAuthHeaders()
        });
        const rawMarks = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.transferMarks)
            ? response.data.transferMarks
            : (Array.isArray(response.data?.data) ? response.data.data : []);
        this.transferMarks = rawMarks
          .map(mark => this.normalizeTransferMark(mark))
          .filter(Boolean);
      } catch (error) {
        console.error('Erro ao carregar transfer marks:', error);
        this.transferMarks = [];
      }
    },

    async handleConversationTransferred(transferData) {
      if (!transferData || typeof transferData !== 'object') return;

      const isTransferredAway = String(transferData?.fromUserId) === String(this.currentUser.id)
        && String(transferData?.toUserId) !== String(this.currentUser.id);

      const isTransferredToMe = String(transferData?.toUserId) === String(this.currentUser.id)
        || (transferData?.departmentId && String(this.currentUser.departmentId) === String(transferData.departmentId));

      await this.loadTransferMarks();
      await this.fetchChats(true);

      if (isTransferredAway) {
        this.showSnackbar('Conversa transferida com sucesso', 'success');
        return;
      }

      if (isTransferredToMe) {
        this.showSnackbar('Você recebeu uma conversa transferida', 'info');
      }
    },

    handleTransferStatusUpdate(transferData) {
      const transfer = this.transfers.find(t => t.id === transferData.id);
      if (transfer) {
        transfer.status = transferData.status;
      }
    },

    handleRealtimePayload(message, forcedConversationId = null) {
      if (!message || typeof message !== 'object') return;
      const msgObj = this.normalizeIncomingMessage(
        message,
        forcedConversationId
          || message?.conversationId
          || message?.remoteJid
          || message?.contactId
          || message?.chatId
          || message?.key?.remoteJid
      );
      if (!msgObj.conversationId) return;

      const isActiveConversation = this.convKey(this.selectedConversation?.id) === this.convKey(msgObj.conversationId);
      const isNearBottom = this.isNearBottom();
      const shouldAutoScroll = isActiveConversation && (msgObj.fromMe || isNearBottom);
      const existingConversation = this.resolveConversationByAnyId(msgObj.conversationId);

      if (msgObj.fromMe && this.reconcileSentMessageWithTemp(msgObj)) {
        return;
      }

      if (!existingConversation && !isActiveConversation) {
        this.fetchChats();
        return;
      }

      this.appendMessage(msgObj.conversationId, msgObj, { autoScroll: shouldAutoScroll });
      this.updateConversationWithMessage(msgObj.conversationId, msgObj, this.unwrapIncomingPayload(message));

      if (isActiveConversation && !shouldAutoScroll) {
        this.showScrollButton = true;
        this.hasNewMessages = true;
      }
    },

    handleNewMessage(message) {
      if (!message || typeof message !== 'object') return;
      this.handleRealtimePayload(
        message,
        message?.conversationId
          || message?.remoteJid
          || message?.contactId
          || message?.chatId
          || message?.key?.remoteJid
          || message?.message?.conversationId
          || message?.message?.remoteJid
      );
    },

    async selectConversation(conversation) {
      const conversationId = this.convKey(conversation?.id);
      if (!conversationId) return;

      if (this.joinedConversationId && this.joinedConversationId !== conversationId) {
        this.leaveConversationRoom(this.joinedConversationId);
      }

      this.selectedConversation = conversation;
      conversation.unreadCount = 0;
      this.messagesError = null;
      this.hasNewMessages = false;
      this.showScrollButton = false;

      const cachedMessages = this.getConversationMessages(conversationId);
      this.messages = cachedMessages;
      this.messagesLoading = cachedMessages.length === 0;

      this.joinConversationRoom(conversationId);
      this.fetchChatHistory(conversationId, cachedMessages.length === 0);

      if (this.socket?.connected) {
        this.socket.emit('whatsapp:get-profile-pic', conversationId);
        this.socket.emit('whatsapp:get-contact-info', conversationId);
      }

      this.$nextTick(() => this.scrollToBottom());
    },

    async sendMessage() {
      const content = (this.messageText || '').trim();
      if (!content || !this.selectedConversation) return;

      const conversationId = this.convKey(this.selectedConversation.id);
      const tempMessage = {
        id: `temp_text_${Date.now()}_${Math.random().toString(16).slice(2)}`,
        senderId: this.currentUser.id,
        content,
        createdAt: this.normalizeTimestamp(Date.now()),
        timestamp: this.normalizeTimestamp(Date.now()),
        status: 'SENDING',
        type: 'text',
        fromMe: true,
        hasMedia: false,
        media: null,
        conversationId
      };

      this.appendMessage(conversationId, tempMessage);
      this.updateConversationWithMessage(conversationId, tempMessage, { fromMe: true });

      this.messageText = '';
      this.isSending = true;

      if (!this.socket?.connected) {
        tempMessage.status = 'ERROR';
        this.isSending = false;
        this.showSnackbar('Socket desconectado. Não foi possível enviar a mensagem.', 'error');
        return;
      }

      this.queuePendingAck('message', conversationId, tempMessage.id);

      this.socket.emit('whatsapp:send-message', {
        to: conversationId,
        conversationId,
        content
      });
    },

    async openTransferDialog() {
      if (!this.selectedConversation) return;
      
      this.transferData = {
        type: 'user',
        toUserId: null,
        departmentId: null,
        notes: ''
      };
      
      this.transferDialog = true;
      await this.loadUsers();
      await this.loadDepartments();
    },

    async loadUsers() {
      this.loadingUsers = true;
      try {
        const response = await axios.get(`${this.apiUrl}/all-users`, {
          headers: this.getAuthHeaders()
        });
        const data = response.data;

        let users = [];
        if (Array.isArray(data)) {
          users = data;
        } else if (data.userall) {
          users = data.userall;
        } else if (data.users) {
          users = data.users;
        } else if (data.departament && Array.isArray(data.departament.users)) {
          users = data.departament.users;
        } else if (data.data && Array.isArray(data.data)) {
          users = data.data;
        }

        this.availableUsers = users.filter(u => String(u.id) !== String(this.currentUser.id));
      } catch (error) {
        console.error('Erro ao carregar usuários:', error);
        this.showSnackbar('Erro ao carregar usuários', 'error');
        this.availableUsers = [];
      } finally {
        this.loadingUsers = false;
      }
    },

    async loadDepartments() {
      this.loadingDepartments = true;
      try {
        const response = await axios.get(`${this.apiUrl}/departments`, {
          headers: this.getAuthHeaders()
        });
        const data = response.data;

        let deps = [];
        if (Array.isArray(data)) {
          deps = data;
        } else if (data.users) {
          deps = data.users;
        } else if (data.departament) {

          if (Array.isArray(data.departament)) deps = data.departament;
          else if (Array.isArray(data.departament.users)) deps = data.departament.users;
          else if (data.departament.users) deps = data.departament.users;
        } else if (data.data && Array.isArray(data.data)) {
          deps = data.data;
        }

        this.availableDepartments = deps.map(d => ({
          id: d.id,
          name: d.name || d.title || d.description || `Departamento ${d.id}`
        }));
      } catch (error) {
        console.error('Erro ao carregar departamentos:', error);
        this.showSnackbar('Erro ao carregar departamentos', 'error');
        this.availableDepartments = [];
      } finally {
        this.loadingDepartments = false;
      }
    },

    async loadTransferHistory() {
      if (!this.selectedConversation) return;

      this.loadingTransferHistory = true;
      try {
        const response = await axios.get(`${this.apiUrl}/transfer-marks`, {
          headers: this.getAuthHeaders()
        });
        const data = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.transferMarks)
            ? response.data.transferMarks
            : (Array.isArray(response.data?.data) ? response.data.data : []);
        const selectedConversationId = this.convKey(this.selectedConversation.id);
        const selectedComparable = this.comparableConversationId(selectedConversationId);

        const transfers = data
          .map(item => this.normalizeTransferMark(item))
          .filter(Boolean)
          .filter(item => (
            this.convKey(item.conversationId) === selectedConversationId
            || this.convKey(item.sourceConversationId) === selectedConversationId
            || this.comparableConversationId(item.conversationId) === selectedComparable
            || this.comparableConversationId(item.sourceConversationId) === selectedComparable
          ));

        this.transfers = transfers.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      } catch (error) {
        console.error('Erro ao carregar histórico de transferências:', error);
        this.transfers = [];
      } finally {
        this.loadingTransferHistory = false;
      }
    },

    async confirmTransfer() {
      if (!this.selectedConversation || !this.$refs.transferForm) return;

      const formValidation = await this.$refs.transferForm.validate();
      const isValid = typeof formValidation === 'boolean'
        ? formValidation
        : Boolean(formValidation?.valid);

      if (!isValid) return;

      this.transferring = true;

      try {
        const conversationId = this.convKey(this.selectedConversation.id);
        const transferPayload = {
          fromUserId: this.currentUser.id,
          notes: this.transferData.notes || null
        };

        if (this.transferData.type === 'user') {
          if (!this.transferData.toUserId) {
            this.showSnackbar('Selecione um usuário para transferir.', 'error');
            return;
          }
          transferPayload.toUserId = this.transferData.toUserId;
        } else {
          if (!this.transferData.departmentId) {
            this.showSnackbar('Selecione um departamento para transferir.', 'error');
            return;
          }
          transferPayload.departmentId = this.transferData.departmentId;
        }

        await axios.post(
          `${this.apiUrl}/create-transfer-mark/${encodeURIComponent(conversationId)}`,
          transferPayload,
          {
            headers: this.getAuthHeaders({
              'x-user-id': String(this.currentUser.id)
            })
          }
        );

        await this.loadTransferMarks();
        await this.fetchChats(true);

        const stillVisibleConversation = this.resolveConversationByAnyId(conversationId);
        if (stillVisibleConversation) {
          this.selectedConversation = stillVisibleConversation;
        } else {
          if (this.joinedConversationId) this.leaveConversationRoom(this.joinedConversationId);
          this.selectedConversation = null;
          this.messages = [];
        }

        this.transferDialog = false;
        this.showSnackbar('Conversa transferida com sucesso', 'success');

      } catch (error) {
        console.error('Erro ao transferir conversa:', error);
        const backendMessage = error?.response?.data?.message || error?.response?.data?.error;
        this.showSnackbar(backendMessage || 'Erro ao transferir conversa', 'error');
      } finally {
        this.transferring = false;
      }
    },

    async removeTransferMark() {
      if (!this.selectedConversation) return;

      const conversationId = this.convKey(this.selectedConversation.id);
      if (!conversationId) return;

      this.transferring = true;
      try {
        await axios.delete(
          `${this.apiUrl}/remove-transfer-mark/${encodeURIComponent(conversationId)}`,
          {
            headers: this.getAuthHeaders({
              'x-user-id': String(this.currentUser.id)
            })
          }
        );

        await this.loadTransferMarks();
        await this.fetchChats(true);

        const refreshedConversation = this.resolveConversationByAnyId(conversationId);
        if (refreshedConversation) {
          this.selectedConversation = refreshedConversation;
        } else {
          if (this.joinedConversationId) this.leaveConversationRoom(this.joinedConversationId);
          this.selectedConversation = null;
          this.messages = [];
        }

        this.showSnackbar('Marcação de transferência removida com sucesso', 'success');
      } catch (error) {
        console.error('Erro ao remover marca de transferência:', error);
        const backendMessage = error?.response?.data?.message || error?.response?.data?.error;
        this.showSnackbar(backendMessage || 'Erro ao remover marca de transferência', 'error');
      } finally {
        this.transferring = false;
      }
    },

    getTransferStatusColor(status) {
      switch (status) {
        case 'PENDING': return 'orange';
        case 'ACCEPTED': return 'green';
        case 'REJECTED': return 'red';
        default: return 'grey';
      }
    },

    getTransferStatusText(status) {
      switch (status) {
        case 'PENDING': return 'Pendente';
        case 'ACCEPTED': return 'Aceita';
        case 'REJECTED': return 'Rejeitada';
        default: return 'Desconhecido';
      }
    },

    showSnackbar(message, color = 'success') {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },

    cleanContactName(jid) {
      if (!jid) return "Contato desconhecido";
      const cleaned = jid.replace(/@s\.whatsapp\.net/, "").replace(/@g\.us/, "").replace(/@c\.us/, "");
      if (/^\d+$/.test(cleaned)) {
        return `+${cleaned}`;
      }
      return cleaned || "Contato";
    },

    sortConversations() {
      this.conversations.sort((a, b) => {
        const ta = new Date(a.lastMessage?.createdAt || a.timestamp || 0).getTime();
        const tb = new Date(b.lastMessage?.createdAt || b.timestamp || 0).getTime();
        return tb - ta;
      });
    },

    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) { 
        container.scrollTop = container.scrollHeight; 
        this.showScrollButton = false; 
        this.hasNewMessages = false;
      }
    },

    isNearBottom(threshold = 80) {
      const container = this.$refs.messagesContainer;
      if (!container) return true;
      return container.scrollTop + container.clientHeight >= container.scrollHeight - threshold;
    },

    handleScroll() {
      const container = this.$refs.messagesContainer;
      if (!container) return;
      this.showScrollButton = container.scrollTop + container.clientHeight < container.scrollHeight - 100;
      if (!this.showScrollButton) {
        this.hasNewMessages = false;
      }
    },

    getStatusColor(status) {
      switch (status) { 
        case 'SENDING': 
        case 'SENT': return 'grey'; 
        case 'DELIVERED': return 'blue'; 
        case 'READ': return 'green'; 
        case 'ERROR': return 'red';
        default: return 'grey'; 
      }
    },

    getStatusIcon(status) {
      switch (status) { 
        case 'SENDING': return 'fas fa-clock'; 
        case 'SENT': return 'fas fa-check'; 
        case 'DELIVERED': return 'fas fa-check-double'; 
        case 'READ': return 'fas fa-check-double'; 
        case 'ERROR': return 'fas fa-exclamation-circle';
        default: return 'fas fa-clock'; 
      }
    },

    formatTime(ts) { 
      if (!ts) return ''; 
      const d = new Date(ts); 
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); 
    },

    formatClock(ts) {
      if (!ts) return '';
      const d = new Date(ts);
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },

    formatDateTime(ts) {
      if (!ts) return '';
      const d = new Date(ts);
      return d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getLastSeen() { 
      return 'última vez hoje às 12:00'; 
    }
  },

  beforeUnmount() {
    if (this.typingTimer) clearTimeout(this.typingTimer);
    if (this.joinedConversationId) this.leaveConversationRoom(this.joinedConversationId);
    this.messages = [];
    this.messagesByConversation = Object.create(null);
    if (this.socket) this.socket.disconnect();
  }
};
</script>

<style>
        :root {
            --bg: #f0f2f5;
            --panel-bg: #ffffff;
            --chat-bg: #efeae2;
            --text: #111b21;
            --muted: #667781;
            --accent: #25d366;
            --accent-2: #128c7e;
            --shadow-sm: 0 1px 3px rgba(0,0,0,0.08);
            --radius-lg: 12px;
        }

        .v-application {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial !important;
        }

        .file-preview {
          display: flex;
          align-items: center;
          background: #f0f0f0;
          border-radius: 8px;
          padding: 8px;
          margin-bottom: 8px;
        }

        .file-preview-icon {
          margin-right: 8px;
        }

        .file-preview-info {
          flex-grow: 1;
        }

        .file-preview-name {
          font-size: 14px;
          font-weight: 500;
        }

        .file-preview-size {
          font-size: 12px;
          color: #666;
        }

        .whatsapp-root {
            height: 100vh;
            min-height: 0;
            display: flex;
            flex-direction: column;
        }

        .whatsapp-root.whatsapp-embedded {
            height: 100%;
        }

        .whatsapp-container {
            height: 100%;
            flex: 1;
            min-height: 0;
            background: var(--bg);
            display: flex;
            flex-direction: column;
            color: var(--text);
            -webkit-font-smoothing: antialiased;
        }

        .whatsapp-main {
            flex: 1;
            display: flex;
            height: 100%;
            min-height: 0;
            overflow: hidden;
        }

        /* Left panel - conversations */
        .left-panel {
            width: 360px;
            min-width: 280px;
            background: var(--panel-bg);
            border-right: 1px solid #e9edef;
            display: flex;
            flex-direction: column;
            box-shadow: var(--shadow-sm);
        }

        /* Header */
        .profile-header {
            height: 64px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0 16px;
            background: #f0f2f5;
            border-bottom: 1px solid #e9edef;
        }

        .profile-header p {
            margin: 0 !important;
            font-size: 23px !important;
            font-weight: 600 !important;
            color: #1DAA61 !important;
        }

        /* Search */
        .search-container {
            padding: 10px 12px;
            background: #f0f2f5;
            border-bottom: 1px solid #e9edef;
        }

        .search-box {
            background: var(--panel-bg);
            border-radius: 10px;
            height: 42px;
            display: flex;
            align-items: center;
            padding: 6px 10px;
            border: 1px solid #e9edef;
            transition: box-shadow .15s ease, border-color .15s ease;
        }

        .search-box:focus-within {
            border-color: rgba(37, 211, 102, 0.25);
            box-shadow: 0 4px 12px rgba(37, 211, 102, 0.06);
        }

        .search-icon {
            margin-right: 8px;
            color: #7b8a90 !important;
        }

        .search-input {
            flex: 1;
            border: none;
            outline: none;
            font-size: 14px;
            color: var(--text);
            background: transparent;
        }

        .search-input::placeholder {
            color: #93a3a8;
        }

        /* Filter tabs */
        .filter-tabs {
            display: flex;
            gap: 8px;
            padding: 10px 12px;
            background: #f0f2f5;
            border-bottom: 1px solid #e9edef;
            overflow-x: auto;
        }

        .filter-tab {
            position: relative;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 6px 12px;
            color: #54656f;
            cursor: pointer;
            white-space: nowrap;
            font-weight: 500;
            font-size: 13px;
            border-radius: 999px;
            border: 1px solid #e9eef0;
            background: #fff;
            transition: all .12s ease;
        }

        .filter-tab:hover {
            transform: translateY(-1px);
            box-shadow: 0 2px 6px rgba(0,0,0,0.03);
        }

        .filter-tab.active {
            color: #ffffff !important;
            background: var(--accent) !important;
            border-color: var(--accent);
        }

        /* Conversations list */
        .conversations-container {
            flex: 1;
            overflow-y: auto;
            background: var(--panel-bg);
            min-width: 0;
            padding-bottom: 8px;
        }

        .conversation-item {
            display: flex;
            align-items: center;
            padding: 12px 16px;
            cursor: pointer;
            gap: 12px;
            transition: background-color .12s ease;
            border-bottom: 1px solid rgba(0,0,0,0.03);
        }

        .conversation-item:hover {
            background: rgba(0,0,0,0.02);
        }

        .conversation-item.selected {
            background: rgba(37,211,102,0.06);
            border-left: 3px solid var(--accent);
        }

        .conversation-avatar .v-avatar {
            flex-shrink: 0;
        }

        .conversation-content {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 6px;
        }

        .conversation-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 8px;
        }

        .conversation-name {
            font-size: 15px;
            font-weight: 600;
            color: var(--text);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .conversation-footer {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 8px;
        }

        .last-message {
            font-size: 13px;
            color: var(--muted);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            display: flex;
            align-items: center;
            gap: 6px;
            min-width: 0;
        }

        .status-icon {
            flex-shrink: 0;
        }

        .you-indicator {
            font-weight: 500;
            color: var(--muted);
        }

        .conversation-badges .v-badge {
            background: var(--accent) !important;
        }

        /* Right panel - chat */
        .right-panel {
            flex: 1;
            display: flex;
            flex-direction: column;
            min-width: 0;
            background: var(--chat-bg);
        }

        .chat-container {
            display: flex;
            flex-direction: column;
            height: 100%;
            min-width: 0;
        }

        /* Chat header */
        .chat-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px 16px;
            height: 72px;
            background: #f0f2f5;
            border-bottom: 1px solid #e9edef;
            box-shadow: var(--shadow-sm);
        }

        .chat-contact-info {
            display: flex;
            align-items: center;
            gap: 12px;
            min-width: 0;
        }

        .contact-details {
            display: flex;
            flex-direction: column;
            min-width: 0;
        }

        .contact-name {
            font-size: 16px;
            font-weight: 600;
            color: var(--text);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .contact-status {
            font-size: 13px;
            color: var(--muted);
        }

        .chat-actions .v-btn {
            color: #5b6b71 !important;
        }

        .chat-actions .v-btn:hover {
            background: rgba(37,211,102,0.06) !important;
            color: var(--accent) !important;
        }

        /* Messages area */
        .chat-messages {
            flex: 1;
            padding: 20px 28px;
            overflow-y: auto;
            overflow-x: hidden;
            display: flex;
            flex-direction: column;
            gap: 10px;
            min-width: 0;
            background: var(--chat-bg);
            background-image: 
                radial-gradient(circle at 20% 50%, rgba(120, 119, 116, 0.03) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(120, 119, 116, 0.03) 0%, transparent 50%),
                radial-gradient(circle at 40% 80%, rgba(120, 119, 116, 0.03) 0%, transparent 50%);
        }

        .messages-container {
            display: flex;
            flex-direction: column;
            gap: 8px;
            min-width: 0;
        }

        .messages-loading,
        .messages-error {
            min-height: 220px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 10px;
            color: #54656f;
            text-align: center;
        }

        .new-message-box {
            position: sticky;
            bottom: 8px;
            align-self: center;
            display: inline-flex;
            align-items: center;
            padding: 8px 14px;
            border: 0;
            border-radius: 999px;
            background: #25d366;
            color: #fff;
            font-size: 12px;
            font-weight: 600;
            cursor: pointer;
            box-shadow: 0 6px 14px rgba(0, 0, 0, 0.18);
            z-index: 3;
        }

        .message {
            display: flex;
            flex-direction: column;
            max-width: 72%;
            padding: 0;
            border-radius: 12px;
            word-break: break-word;
            font-size: 14px;
            line-height: 1.4;
            animation: fadeInUp .16s ease;
            align-self: flex-start;
        }

        .message.sent {
            align-self: flex-end;
            max-width: 78%;
        }

        .message.received {
            align-self: flex-start;
        }

        .message-content {
            padding: 10px 12px;
            border-radius: 10px;
            position: relative;
            box-shadow: 0 1px 0.5px rgba(153, 144, 144, 0.06);
            background: #fff;
        }

        .message.sent .message-content {
            background: #dcf8c6;
        }

        .message.received .message-content {
            background: #ffffff;
        }

        .message-text {
            color: var(--text);
            font-size: 14px;
            margin: 0 0 6px 0;
            word-break: break-word;
        }

        .message-time {
            font-size: 11px;
            color: var(--muted);
            display: flex;
            gap: 6px;
            align-self: flex-end;
            align-items: center;
        }

        .message-content img {
            max-width: 200px;
            border-radius: 8px;
            display: block;
            margin-bottom: 8px;
        }

        /* Transfer notification */
        .transfer-notification {
            display: flex;
            justify-content: center;
            margin: 12px 0;
        }

        .enhanced-preview {
        background: rgba(37, 211, 102, 0.08);
        backdrop-filter: blur(8px);
        border-top: 1px solid rgba(37, 211, 102, 0.15);
        border-bottom: 1px solid rgba(37, 211, 102, 0.15);
        animation: slideInPreview 0.2s ease;
        }

        .transfer-content {
            background: rgba(255,193,7,0.08);
            border: 1px solid rgba(255,193,7,0.22);
            border-radius: 10px;
            padding: 8px 12px;
            font-size: 13px;
            color: #ad6100;
            display: flex;
            gap: 8px;
            align-items: center;
            max-width: 78%;
        }

        /* Preview bubble */
        .message-preview {
            padding: 12px 16px;
            background: rgba(58, 73, 97, 0.9);
            border-top: 1px solid #eaeef0;
            border-bottom: 1px solid #eaeef0;
            display: flex;
            justify-content: flex-end;
        }

        .preview-container {
            max-width: 72%;
        }

        .preview-message .message-content {
            background: #d9fdd3;
            opacity: 0.95;
        }

        /* Chat input area */
        .chat-input {
            display: flex;
            align-items: flex-end;
            padding: 12px 16px;
            background: #f0f2f5;
            border-top: 1px solid #edf2f3;
            gap: 10px;
            min-height: 64px;
            position: relative;
        }

        .message-input-container {
            flex: 1;
            background: #ffffff;
            border-radius: 22px;
            padding: 8px 10px;
            border: 1px solid #e8eef0;
            display: flex;
            align-items: flex-end;
            min-height: 42px;
            position: relative;
            transition: box-shadow .12s ease, border-color .12s ease;
        }

        .message-input-container:focus-within {
            border-color: rgba(37,211,102,0.3);
            box-shadow: 0 6px 18px rgba(37,211,102,0.06);
        }

        .message-image .image-caption {
            margin-top: 8px;
            font-size: 14px;
            color: #54656f;
            font-style: italic;
        }

        .audio-waveform {
            flex: 1;
            height: 20px;
            background: rgba(0, 0, 0, 0.1);
            border-radius: 10px;
            position: relative;
            overflow: hidden;
        }

        .audio-waveform .audio-progress {
            height: 100%;
            background: #25d366;
            border-radius: 10px;
            transition: width 0.1s ease;
        }

        .audio-duration {
            font-size: 12px;
            color: #54656f;
            min-width: 35px;
        }

        .message-video .video-caption {
            margin-top: 8px;
            font-size: 14px;
            color: #54656f;
            font-style: italic;
        }

        .message-document .document-card {
            max-width: 300px;
            margin-bottom: 4px;
        }

        .message-document .document-caption {
            margin-top: 8px;
            font-size: 14px;
            color: #54656f;
            font-style: italic;
        }

        .message-sticker {
            padding: 0 !important;
        }

        .message-unsupported {
            display: flex;
            align-items: center;
            color: #54656f;
            font-style: italic;
            padding: 8px 12px;
            background: rgba(244, 67, 54, 0.1);
            border-radius: 8px;
        }

        /* Mensagens enviadas (direita) */
        .message.sent .message-audio .audio-player {
            background: rgba(255, 255, 255, 0.2);
        }

        /* Mensagens recebidas (esquerda) */
        .message.received .message-audio .audio-player {
            background: rgba(0, 0, 0, 0.05);
        }

        .message-audio .audio-player {
            display: flex;
            align-items: center;
            gap: 8px;
            background: rgba(0, 0, 0, 0.05);
            padding: 8px 12px;
            border-radius: 18px;
            min-width: 200px;
        }

        .message-input {
            flex: 1;
            border: none;
            outline: none;
            resize: none;
            background: transparent;
            font-size: 15px;
            padding: 6px 8px;
            line-height: 1.4;
            color: var(--text);
            min-height: 20px;
            max-height: 140px;
            font-family: inherit;
        }

        .enhanced-textarea {
            overflow: auto;
        }

        .enhanced-textarea::-webkit-scrollbar {
            width: 6px;
        }

        .enhanced-textarea::-webkit-scrollbar-thumb {
            background: rgba(0,0,0,0.12);
            border-radius: 3px;
        }

        .char-counter {
            position: absolute;
            bottom: -18px;
            right: 12px;
            font-size: 11px;
            color: var(--muted);
        }

        .input-action-btn {
            width: 40px !important;
            height: 40px !important;
            min-width: 40px !important;
            color: #5b6b71 !important;
            transition: all .12s ease;
        }

        .input-action-btn:hover {
            background: rgba(37,211,102,0.06) !important;
            color: var(--accent) !important;
        }

        .send-btn {
            width: 44px !important;
            height: 44px !important;
            min-width: 44px !important;
            border-radius: 50% !important;
            box-shadow: 0 2px 6px rgba(0,0,0,0.06);
            transition: transform .12s ease, background .12s ease;
            background: transparent;
            color: #5b6b71 !important;
            position: relative;
        }

        .send-btn.active {
            background: var(--accent) !important;
            color: #ffffff !important;
            transform: scale(1.03);
        }

        .send-btn.sending {
            background: var(--accent-2) !important;
            color: #ffffff !important;
        }

        .ripple-effect {
            position: absolute;
            inset: 0;
            border-radius: 50%;
            pointer-events: none;
            opacity: 0;
            transition: opacity .25s ease, transform .25s ease;
            background: rgba(255,255,255,0.3);
        }

        .send-btn:active .ripple-effect {
            opacity: 0.12;
            transform: scale(1.2);
        }

        .typing-indicator {
            position: absolute;
            left: calc(16px + 50px);
            bottom: 64px;
            background: #ffffff;
            padding: 6px 10px;
            border-radius: 16px;
            font-size: 12px;
            color: var(--muted);
            box-shadow: 0 6px 18px rgba(0,0,0,0.06);
            display: flex;
            align-items: center;
            gap: 8px;
            opacity: 0;
            transform: translateY(6px);
            pointer-events: none;
            transition: all .18s ease;
        }

        .typing-indicator.active {
            opacity: 1;
            transform: translateY(0);
            pointer-events: auto;
        }

        .typing-dots {
            display: flex;
            gap: 6px;
            align-items: center;
        }

        .typing-dots span {
            display: inline-block;
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: var(--accent);
            animation: typingDot 1s infinite ease-in-out;
        }

        .typing-dots span:nth-child(2) {
            animation-delay: .12s;
        }

        .typing-dots span:nth-child(3) {
            animation-delay: .24s;
        }

        /* Loading / error */
        .loading-container, .error-container {
            padding: 32px;
            display: flex;
            flex-direction: column;
            gap: 12px;
            align-items: center;
            justify-content: center;
        }

        .welcome-screen {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            gap: 12px;
            text-align: center;
            padding: 28px;
            background: var(--chat-bg);
        }

        .welcome-title {
            font-size: 28px;
            font-weight: 400;
            color: #41525d;
            margin: 0;
        }

        /* Transfer dialog */
        .transfer-dialog {
            border-radius: var(--radius-lg);
            overflow: hidden;
        }

        .transfer-header {
            background: linear-gradient(135deg,var(--accent) 0%, var(--accent-2) 100%);
            color: #ffffff;
            padding: 16px 18px;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .transfer-header h3 {
            margin: 0;
            font-size: 18px;
            font-weight: 600;
        }

        /* Scrollbars */
        .conversations-container::-webkit-scrollbar,
        .chat-messages::-webkit-scrollbar {
            width: 8px;
        }

        .conversations-container::-webkit-scrollbar-thumb,
        .chat-messages::-webkit-scrollbar-thumb {
            background: rgba(0,0,0,0.12);
            border-radius: 6px;
        }

        /* Animations */
        @keyframes typingDot {
            0%, 60%, 100% {
                transform: translateY(0);
                opacity: .45;
            }
            30% {
                transform: translateY(-6px);
                opacity: 1;
            }
        }

        @keyframes fadeInUp {
            from {
                transform: translateY(6px);
                opacity: 0;
            }
            to {
                transform: translateY(0);
                opacity: 1;
            }
        }

        .spin {
            animation: spin 1s linear infinite;
        }

        @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
        }

        /* Responsive */
        @media (max-width: 900px) {
            .left-panel {
                width: 320px;
            }
        }

        @media (max-width: 768px) {
            .left-panel {
                display: none;
            }

            .right-panel {
                width: 100%;
            }

            .chat-messages {
                padding: 12px;
            }

            .message {
                max-width: 88%;
            }

            .typing-indicator {
                left: 14px;
                bottom: 86px;
            }

            .char-counter {
                right: 8px;
            }
        }

        @media (max-width: 480px) {
            .chat-header {
                padding: 10px;
                height: 64px;
            }

            .profile-header p {
                font-size: 18px !important;
            }

            .message-input-container {
                min-height: 40px;
                padding: 6px 8px;
            }

            .chat-messages {
                padding: 10px;
            }

            .message {
                max-width: 92%;
            }
        }

        /* Utility classes */
        .hidden {
            display: none !important;
        }

        .center {
            display: flex;
            align-items: center;
            justify-content: center;
        }

        /* Override Vuetify styles where necessary */
        .v-application .text-body-2 {
            font-size: 14px !important;
        }

        .v-application .text-caption {
            font-size: 12px !important;
        }

        /* Ensure proper spacing in Vuetify components */
        .v-btn--icon.v-btn--density-default {
            width: auto;
            height: auto;
        }

        /* Fix for Vuetify badges */
        .v-badge__badge {
            background: var(--accent) !important;
            color: white !important;
            font-size: 11px !important;
            font-weight: 700 !important;
            min-width: 18px !important;
            height: 18px !important;
        }

        /* Fix for chips */
        .v-chip.v-chip--size-x-small {
            font-size: 10px !important;
            height: 20px !important;
        }
</style>
